import React from "react";
import { TextField, Button, Typography, Box, Checkbox, Collapse, Popover } from "@mui/material";
import { ChevronDown, Search } from "lucide-react";

export interface TreeOption {
    id: string;
    label: string;
    children?: TreeOption[];
    isSpecial?: boolean; // For special options like "Select All", "No Bank Account"
    specialType?: 'selectAll' | 'noBankAccount';
}

export interface CheckboxTreeProps {
    onChange?: (checkedIds: string[]) => void;
    value?: string[];
    treeOptions: TreeOption[];
    placeholder?: string;
    countLabel?: string; // e.g., "banks", "categories" for count display
}

const CheckboxTree: React.FC<CheckboxTreeProps> = ({ onChange, treeOptions, placeholder, value, countLabel }) => {
    const keyRef = React.useRef<string>(Math.random().toString(36).substring(2, 15));
    const [filterValue, setFilterValue] = React.useState<string>('');
    const [checkedIds, setCheckedIds] = React.useState<string[]>(value || []);
    const [expandedIds, setExpandedIds] = React.useState<{ [id: string]: boolean }>({});
    const [localTreeOptions, setLocalTreeOptions] = React.useState<TreeOption[]>(treeOptions);
    const [open, setOpen] = React.useState<boolean>(false);

    React.useEffect(() => {
        setCheckedIds(value || []);
    }, [value]);

    React.useEffect(() => {
        setLocalTreeOptions(treeOptions);
        
        // Initialize all expandable items as expanded by default
        const initializeExpanded = (options: TreeOption[]): { [id: string]: boolean } => {
            const expanded: { [id: string]: boolean } = {};
            const processOptions = (opts: TreeOption[]) => {
                opts.forEach(option => {
                    if (option.children && option.children.length > 0) {
                        expanded[option.id] = true;
                        processOptions(option.children);
                    }
                });
            };
            processOptions(options);
            return expanded;
        };
        
        setExpandedIds(initializeExpanded(treeOptions));
    }, [treeOptions]);

    const key = keyRef.current;

    // Generate display text for selected values
    const getSelectedText = React.useMemo(() => {
        if (!checkedIds.length) return placeholder || 'Select...';
        
        // Get labels for selected IDs (excluding special options)
        const getOptionLabel = (options: TreeOption[], targetId: string | number): string | null => {
            for (const option of options) {
                // Check both exact match and string conversions in both directions
                if (option.id === targetId || 
                    String(option.id) === String(targetId) ||
                    Number(option.id) === Number(targetId)) {
                    return option.label;
                }
                if (option.children) {
                    const found = getOptionLabel(option.children, targetId);
                    if (found) return found;
                }
            }
            return null;
        };

        // Get option info including specialType
        const getOption = (options: TreeOption[], targetId: string | number): TreeOption | null => {
            for (const option of options) {
                if (option.id === targetId || 
                    String(option.id) === String(targetId) ||
                    Number(option.id) === Number(targetId)) {
                    return option;
                }
                if (option.children) {
                    const found = getOption(option.children, targetId);
                    if (found) return found;
                }
            }
            return null;
        };
        
        // Get labels excluding special selectAll options
        const countableCheckedIds = checkedIds.filter(id => {
            const option = getOption(localTreeOptions, id);
            return option?.specialType !== 'selectAll' && option?.specialType !== 'noBankAccount' && !option?.isSpecial;
        });

        const selectedLabels = countableCheckedIds
            .map(id => getOptionLabel(localTreeOptions, id))
            .filter(Boolean) as string[];
        
        if (!selectedLabels.length) return placeholder || 'Select...';
        
        // If multiple items selected and we have a countLabel, show count
        if (selectedLabels.length > 1 && countLabel) {
            return `${selectedLabels.length} ${countLabel} selected`;
        }
        
        // Single item or no countLabel - show the label(s)
        const joinedText = selectedLabels.join(', ');
        
        // Truncate if too long (adjust maxLength as needed)
        const maxLength = 25;
        if (joinedText.length > maxLength) {
            return joinedText.substring(0, maxLength) + '...';
        }
        
        return joinedText;
    }, [checkedIds, localTreeOptions, placeholder, countLabel]);

    const handleCheck = (id: string | number, checked: boolean) => {
        const idStr = String(id);
        let newCheckedIds: string[];

        // Find the option to check if it's a special type
        const findOption = (options: TreeOption[], targetId: string): TreeOption | null => {
            for (const option of options) {
                if (option.id === targetId) return option;
                if (option.children) {
                    const found = findOption(option.children, targetId);
                    if (found) return found;
                }
            }
            return null;
        };

        // Helper function to get all descendant IDs
        const getAllDescendantIds = (option: TreeOption): string[] => {
            const ids: string[] = [String(option.id)];
            if (option.children) {
                option.children.forEach(child => {
                    ids.push(...getAllDescendantIds(child));
                });
            }
            return ids;
        };

        // Helper function to update parent selections based on children
        const updateParentSelections = (checkedIds: string[]): string[] => {
            const result = [...checkedIds];
            
            // Recursive function to check and update parents
            const checkParents = (options: TreeOption[]) => {
                options.forEach(option => {
                    if (option.children) {
                        // Check children first (bottom-up)
                        checkParents(option.children);
                        
                        // Check if all children are selected
                        const allChildrenSelected = option.children.every(child => 
                            result.includes(String(child.id))
                        );
                        
                        const isParentSelected = result.includes(String(option.id));
                        
                        if (allChildrenSelected && !isParentSelected) {
                            // Auto-select parent if all children are selected
                            result.push(String(option.id));
                        } else if (!allChildrenSelected && isParentSelected) {
                            // Auto-deselect parent if not all children are selected
                            const index = result.indexOf(String(option.id));
                            if (index > -1) {
                                result.splice(index, 1);
                            }
                        }
                    }
                });
            };
            
            checkParents(localTreeOptions);
            return result;
        };

        const clickedOption = findOption(localTreeOptions, idStr);

        if (clickedOption?.specialType === 'selectAll') {
            if (checked) {
                // Select all non-special options
                const getAllNonSpecialIds = (options: TreeOption[]): string[] => {
                    const ids: string[] = [];
                    options.forEach(option => {
                        if (!option.isSpecial) {
                            ids.push(option.id);
                        }
                        if (option.children) {
                            ids.push(...getAllNonSpecialIds(option.children));
                        }
                    });
                    return ids;
                };
                const allNonSpecialIds = getAllNonSpecialIds(localTreeOptions);
                newCheckedIds = allNonSpecialIds;
            } else {
                // Deselect all non-special options
                newCheckedIds = checkedIds.filter(id => {
                    const option = findOption(localTreeOptions, id);
                    return option?.isSpecial;
                });
            }
        } else {
            if (checked) {
                // When checking an option, also check all its children
                const idsToAdd = clickedOption ? getAllDescendantIds(clickedOption) : [idStr];
                newCheckedIds = [...checkedIds.filter(cid => !idsToAdd.includes(cid)), ...idsToAdd];
            } else {
                // When unchecking an option, also uncheck all its children and potentially parents
                const idsToRemove = clickedOption ? getAllDescendantIds(clickedOption) : [idStr];
                newCheckedIds = checkedIds.filter(cid => !idsToRemove.includes(cid));
            }
            
            // Update parent selections based on children state
            newCheckedIds = updateParentSelections(newCheckedIds);
        }

        setCheckedIds(newCheckedIds);
        onChange && onChange(newCheckedIds);
    };

    const handleToggleExpand = (id: string) => {
        setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const filterTree = (options: TreeOption[]): TreeOption[] => {
        if (!filterValue.trim()) return options;

        const lower = filterValue.toLowerCase();
        const filterFn = (opt: TreeOption): TreeOption | null => {
            if (opt.label.toLowerCase().includes(lower)) return opt;
            if (opt.children) {
                const filteredChildren = opt.children.map(filterFn).filter(Boolean) as TreeOption[];
                if (filteredChildren.length) return { ...opt, children: filteredChildren };
            }
            return null;
        };
        return options.map(filterFn).filter(Boolean) as TreeOption[];
    };

    const renderTree = (
        options: TreeOption[],
        checked: string[],
        onCheck: (id: string, checked: boolean) => void,
        level = 0,
        parentLast: boolean[] = []
    ) => {
        return options.map((option, idx) => {
            const hasChildren = option.children && option.children.length > 0;
            const expanded = !!expandedIds[option.id];
            const isLast = idx === options.length - 1;

            // For "Select All", check if all non-special options are selected
            let isChecked = checked.includes(option.id) || checked.includes(String(option.id));
            let isIndeterminate = false;
            
            if (option.specialType === 'selectAll') {
                const getAllNonSpecialIds = (opts: TreeOption[]): string[] => {
                    const ids: string[] = [];
                    opts.forEach(opt => {
                        if (!opt.isSpecial) {
                            ids.push(opt.id);
                        }
                        if (opt.children) {
                            ids.push(...getAllNonSpecialIds(opt.children));
                        }
                    });
                    return ids;
                };
                const allNonSpecialIds = getAllNonSpecialIds(localTreeOptions);
                isChecked = allNonSpecialIds.length > 0 && allNonSpecialIds.every(id => 
                    checked.includes(id) || checked.includes(String(id))
                );
                // Set indeterminate if some but not all are selected
                if (!isChecked) {
                    isIndeterminate = allNonSpecialIds.some(id => 
                        checked.includes(id) || checked.includes(String(id))
                    );
                }
            } else if (hasChildren && !isChecked) {
                // Check if some (but not all) children are selected for regular options
                const hasAnySelectedDescendants = (opt: TreeOption): boolean => {
                    if (checked.includes(opt.id) || checked.includes(String(opt.id))) return true;
                    if (opt.children) {
                        return opt.children.some(hasAnySelectedDescendants);
                    }
                    return false;
                };
                isIndeterminate = option.children!.some(hasAnySelectedDescendants);
            }

            return (
                <Box
                    key={option.id}
                    sx={{
                        position: "relative",
                        ml: `${level * 2}px`,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box display="flex" alignItems="center" mb={0.5} sx={{ position: "relative" }}>
                        {/* connector line */}
                        {level > 0 && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: -4,
                                    top: 12,
                                    width: 12,
                                    height: 2,
                                    borderTop: "1px solid #e0e0e0",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: -30,
                                        width: 2,
                                        height: 30,
                                        borderLeft: "1px solid #e0e0e0",
                                    }}
                                />
                            </Box>
                        )}
                        <Checkbox
                            checked={isChecked}
                            indeterminate={isIndeterminate}
                            onChange={e => onCheck(option.id, e.target.checked)}
                            size="small"
                            sx={{ 
                                p: 0.5, 
                                zIndex: 2, 
                                background: 'var(--color-page-background)', 
                                '&:hover': { background: 'var(--color-container-background)' },
                                ...(option.isSpecial && { 
                                    '& .MuiSvgIcon-root': { 
                                        fontWeight: 'bold',
                                        color: option.specialType === 'selectAll' ? 'var(--color-primary)' : 'var(--color-text-muted)'
                                    } 
                                })
                            }}
                        />
                        <Typography 
                            fontSize={12} 
                            sx={{ 
                                userSelect: "none", 
                                zIndex: 2,
                                fontWeight: option.isSpecial ? 'bold' : 'normal',
                                color: option.specialType === 'selectAll' ? 'var(--color-primary)' : 'inherit'
                            }}
                        >
                            {option.label}
                        </Typography>
                        {hasChildren && (
                            <Button
                                size="small"
                                onClick={() => handleToggleExpand(option.id)}
                                sx={{ minWidth: 20, height: 20, ml: 1, fontSize: 12, p: 0.5, fontWeight: 'bold', zIndex: 2 }}
                            >
                                {expanded ? "−" : "+"}
                            </Button>
                        )}
                    </Box>
                    {hasChildren && (
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box ml={2}>
                                {renderTree(
                                    option.children!,
                                    checked,
                                    onCheck,
                                    level + 1,
                                    [...parentLast, isLast]
                                )}
                            </Box>
                        </Collapse>
                    )}
                </Box>
            );
        });
    };

    return (
        <Box position="relative" display="inline-block">
            <Box
                id={`checkbox-tree-anchor-${key}`}
                sx={{
                    color: 'var(--color-primary)',
                    fontSize: 12,
                    background: 'var(--color-page-background)',
                    width: '100%',
                    height: 30,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                }}
                onClick={() => setOpen(true)}
            >
                <Typography color="var(--color-default-text)" fontSize={11} textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">{getSelectedText}</Typography>
                <ChevronDown size={14} color="#ccc"/>
            </Box>
            <Popover
                open={open}
                anchorEl={document.getElementById(`checkbox-tree-anchor-${key}`)}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        borderRadius: 1,
                        boxShadow: 3,
                        p: 1,
                        minWidth: 150,
                        transform: 'translateX(-100px)',
                    },
                }}
            >
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search..."
                    value={filterValue}
                    onChange={e => setFilterValue(e.target.value)}
                    sx={{ mb: 1, mt: 0.5, borderRadius: 1 }}
                    InputProps={{
                        sx: { border: "none", boxShadow: "none", fontSize: 12 },
                        startAdornment: <Search size={16} color="var(--color-primary)" style={{ marginRight: 8 }} />,
                    }}
                />
                <Box sx={{ maxHeight: 160, overflowY: "auto", mb: 1 }}>
                    {renderTree(filterTree(localTreeOptions), checkedIds, handleCheck)}
                </Box>
            </Popover>
        </Box>
    );
};

export default CheckboxTree;
